import { useTranslation } from 'react-i18next'
import { RadioOptionPicker } from '@/components/common/Form/RadioOptionPicker/RadioOptionPicker'
import { Option } from '@/components/common/Form/types'
import { Pill, Tooltip } from '@/components/atoms'
import * as React from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useActiveManagementMetersQuery } from '../../hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '../../hooks/useActiveManagementSubmetersQuery'
import { IntervalDisplayParam, IntervalReportSettings } from '@/hooks'
import useActiveManagementStore from '../../stores/useActiveManagementStore/useActiveManagementStore'
import {
    ActiveManagementFiltersValues,
    INTERVAL_MAX_METERS_DISPLAY_COUNT,
} from '@/utils/constants'

type DisplaySettingsSelectProps = {
    value: IntervalDisplayParam
    onChange: (display: IntervalDisplayParam) => void
    activeFilters: ActiveManagementFiltersValues
    reportSettings: IntervalReportSettings
}

export const DisplaySettingsSelect: React.FC<DisplaySettingsSelectProps> = ({
    value,
    onChange,
    activeFilters,
    reportSettings,
}) => {
    const { t } = useTranslation('translations')
    const pillRef = useRef<HTMLDivElement>(null)

    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })
    const selectedMeters = activeFilters.meter
    const selectedSubmeters = activeFilters.subMeter

    const setHoveredDisplaySetting = useActiveManagementStore(
        (state) => state.intervalData.setHoveredDisplaySetting
    )

    // Calculate total meters based on filter state:
    // - If specific meters are selected, use the count of selected meters
    // - If no meters are selected (all meters), use the total count of available meters
    const metersCount =
        selectedMeters.length > 0 ? selectedMeters.length : meters?.length || 0

    // Calculate total subMeters based on filter state:
    // - If specific subMeters are selected, use the count of selected subMeters
    // - If no subMeters are selected (all subMeters), use the total count of available subMeters
    const submetersCount =
        selectedSubmeters && selectedSubmeters.length > 0
            ? selectedSubmeters.length
            : subMeters?.length || 0

    const metersDisplayCount = useMemo(() => {
        if (
            reportSettings.dataGrouping === 'compare' &&
            metersCount > INTERVAL_MAX_METERS_DISPLAY_COUNT
        ) {
            return INTERVAL_MAX_METERS_DISPLAY_COUNT
        }
        return metersCount
    }, [reportSettings.dataGrouping, metersCount])

    const metersShowAlert = useMemo(() => {
        return (
            reportSettings.dataGrouping === 'compare' &&
            metersCount > INTERVAL_MAX_METERS_DISPLAY_COUNT
        )
    }, [reportSettings.dataGrouping, metersCount])

    const submetersDisplayCount = useMemo(() => {
        if (
            reportSettings.dataGrouping === 'compare' &&
            submetersCount > INTERVAL_MAX_METERS_DISPLAY_COUNT
        ) {
            return INTERVAL_MAX_METERS_DISPLAY_COUNT
        }
        return submetersCount
    }, [reportSettings.dataGrouping, submetersCount])

    // Show alert for subMeters when in compare mode and count exceeds the limit
    const submetersShowAlert = useMemo(() => {
        return (
            reportSettings.dataGrouping === 'compare' &&
            submetersCount > INTERVAL_MAX_METERS_DISPLAY_COUNT
        )
    }, [reportSettings.dataGrouping, submetersCount])

    const options: Array<Option<IntervalDisplayParam>> = [
        {
            value: 'meters',
            label: t(
                'active_management.interval.report_settings.data_setup.display.meters.label'
            ),
        },
        {
            value: 'subMeters',
            label: t(
                'active_management.interval.report_settings.data_setup.display.subMeters.label'
            ),
        },
    ]

    const onMouseEnter = useCallback(
        (display: IntervalDisplayParam) => {
            setHoveredDisplaySetting(display)
        },
        [setHoveredDisplaySetting]
    )

    const onMouseLeave = useCallback(() => {
        setHoveredDisplaySetting(null)
    }, [setHoveredDisplaySetting])

    const getDisplayMetrics = useCallback(
        (displayValue: IntervalDisplayParam) => ({
            displayCount:
                displayValue === 'subMeters'
                    ? submetersDisplayCount
                    : metersDisplayCount,
            showAlert:
                displayValue === 'subMeters'
                    ? submetersShowAlert
                    : metersShowAlert,
        }),
        [
            metersDisplayCount,
            submetersDisplayCount,
            metersShowAlert,
            submetersShowAlert,
        ]
    )

    const renderLabel = useCallback(
        (option: Option) => {
            const displayValue = option.value as IntervalDisplayParam
            const { displayCount, showAlert } = getDisplayMetrics(displayValue)

            return (
                <div
                    className="flex flex-col"
                    onMouseEnter={() => {
                        onMouseEnter(displayValue)
                    }}
                    onMouseLeave={onMouseLeave}
                >
                    <div className="flex items-center gap-2">
                        <div className="text-label-small text-regular">
                            {option.label}
                        </div>
                        <Tooltip
                            content={t('active_management.interval.alert', {
                                count: INTERVAL_MAX_METERS_DISPLAY_COUNT,
                                type: t(
                                    `active_management.interval.alert_type.${displayValue}`
                                ),
                            })}
                            name={`interval-display-${displayValue}-tooltip`}
                            show={showAlert}
                        >
                            <Pill
                                value={displayCount}
                                icon={showAlert ? 'info' : undefined}
                            />
                        </Tooltip>
                    </div>
                    <div className="text-body-small font-normal text-subtle">
                        {t(
                            `active_management.interval.report_settings.data_setup.display.${displayValue}.subtitle`
                        )}
                    </div>
                </div>
            )
        },
        [onMouseEnter, onMouseLeave, t, getDisplayMetrics]
    )

    const renderValueLabel = useCallback(
        (option: Option) => {
            const displayValue = option.value as IntervalDisplayParam
            const { displayCount, showAlert } = getDisplayMetrics(displayValue)

            return (
                <div className="flex items-center gap-2">
                    <div className="overflow-hidden truncate text-ellipsis text-azure group-hover:text-blue-600 group-active:text-blue-700 group-disabled:text-disabled">
                        {option.label}
                    </div>
                    <div ref={pillRef}>
                        <Tooltip
                            content={t('active_management.interval.alert', {
                                count: INTERVAL_MAX_METERS_DISPLAY_COUNT,
                                type: t(
                                    `active_management.interval.alert_type.${displayValue}`
                                ),
                            })}
                            name="interval-display-meters-tooltip"
                            show={showAlert}
                        >
                            <Pill
                                value={displayCount}
                                icon={showAlert ? 'info' : undefined}
                            />
                        </Tooltip>
                    </div>
                </div>
            )
        },
        [t, getDisplayMetrics]
    )

    return (
        <RadioOptionPicker
            name="display-setting"
            label={t(
                'active_management.interval.report_settings.data_setup.display.label'
            )}
            description={
                <div className="text-body-small text-subtle">
                    {t(
                        `active_management.interval.report_settings.data_setup.display.${value}.description`
                    )}
                </div>
            }
            value={value as string}
            options={options as Array<Option<string>>}
            onChange={(newValue: string) => {
                onChange(newValue as IntervalDisplayParam)
            }}
            renderLabel={renderLabel}
            renderValueLabel={renderValueLabel}
            className="!border-azure !shadow-inset-azure"
        />
    )
}
